// Aufgeklappter Journal-Eintrag: Werte, Notizen, Tranchen, Kursverlauf, Anhaenge
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { LineChart, Line, XAxis, YAxis, Tooltip, ReferenceLine, ResponsiveContainer } from "recharts";
import { apiClient } from "../api/client";
import { CATEGORIES, EMPTY_TRANCHE, api, clampDate, de, labelOf, nowLocal, signed, strategyLabel, toLocalInput } from "./common";

const fmtDT = (s: string) => { const d = new Date(s); return isNaN(d.getTime()) ? "–" : d.toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }); };

function Tranchen({ tradeId, currency }: { tradeId: number; currency: string }) {
  const qc = useQueryClient();
  const [form, setForm] = useState<any>({ ...EMPTY_TRANCHE, datum: nowLocal() });
  const [editId, setEditId] = useState<number | null>(null);
  const [edit, setEdit] = useState<any>({});
  const { data: tr = [] } = useQuery({ queryKey: ["tranchen", tradeId], queryFn: () => api.getTranchen(tradeId) });
  const done = () => { qc.invalidateQueries({ queryKey: ["tranchen", tradeId] }); qc.invalidateQueries({ queryKey: ["journal"] }); };
  const toNums = (x: any) => ({ ...x, kurs: parseFloat(x.kurs), stueckzahl: parseFloat(x.stueckzahl), kosten: parseFloat(x.kosten) || 0, steuer: parseFloat(x.steuer) || 0 });
  const addM = useMutation({ mutationFn: (d: any) => api.addTranche(tradeId, d), onSuccess: () => { done(); setForm({ ...EMPTY_TRANCHE, datum: nowLocal() }); } });
  const delM = useMutation({ mutationFn: (id: number) => api.deleteTranche(tradeId, id), onSuccess: done });
  const updM = useMutation({ mutationFn: ({ id, d }: any) => api.updateTranche(tradeId, id, d), onSuccess: () => { done(); setEditId(null); } });
  const list: any[] = Array.isArray(tr) ? tr : [];
  const kauf = list.filter(t => t.typ === "kauf");
  const qty = kauf.reduce((s, t) => s + t.stueckzahl, 0);
  const avg = qty ? kauf.reduce((s, t) => s + t.kurs * t.stueckzahl, 0) / qty : 0;
  const valid = (x: any) => !isNaN(parseFloat(x.kurs)) && !isNaN(parseFloat(x.stueckzahl)) && x.datum;
  const row = (x: any, set: (v: any) => void) => (
    <div className="tr-form">
      <select value={x.typ} onChange={e => set({ ...x, typ: e.target.value })}><option value="kauf">Kauf</option><option value="verkauf">Verkauf</option></select>
      <input type="datetime-local" value={x.datum} onChange={e => set({ ...x, datum: clampDate(e.target.value) })} />
      <input type="number" step="any" inputMode="decimal" placeholder="Kurs" value={x.kurs} onChange={e => set({ ...x, kurs: e.target.value })} />
      <input type="number" step="any" inputMode="decimal" placeholder="Stück" value={x.stueckzahl} onChange={e => set({ ...x, stueckzahl: e.target.value })} />
      <input type="number" step="any" placeholder="Kosten €" value={x.kosten} onChange={e => set({ ...x, kosten: e.target.value })} />
      <input type="number" step="any" placeholder="Steuer €" value={x.steuer} onChange={e => set({ ...x, steuer: e.target.value })} />
    </div>
  );
  return (
    <div className="jd-sec">
      <div className="jd-sec-head"><span>Tranchen</span>
        {list.length > 0 && <span className="mono">Ø {de(avg)} · {de(qty, 4)} Stk</span>}</div>
      {list.map(t => editId === t.id ? (
        <div key={t.id}>{row(edit, setEdit)}
          <div className="jd-actions"><button className="jf-btn" onClick={() => valid(edit) && updM.mutate({ id: t.id, d: toNums(edit) })}>Speichern</button>
            <button className="jf-btn" onClick={() => setEditId(null)}>Abbrechen</button></div></div>
      ) : (
        <div key={t.id} className="tr-row mono">
          <span className={t.typ === "kauf" ? "up" : "down"}>{t.typ === "kauf" ? "Kauf" : "Verk."}</span>
          <span>{fmtDT(t.datum)}</span><span>{de(t.stueckzahl, 4)} × {de(t.kurs)}</span>
          <span className="tr-sum">{de(t.kurs * t.stueckzahl)} {currency}</span>
          <button className="jf-mini" aria-label="Tranche bearbeiten" onClick={() => { setEditId(t.id); setEdit({ typ: t.typ, datum: toLocalInput(t.datum), kurs: t.kurs, stueckzahl: t.stueckzahl, kosten: t.kosten || 0, steuer: t.steuer || 0 }); }}>✎</button>
          <button className="jf-mini" aria-label="Tranche löschen" onClick={() => { if (window.confirm("Tranche löschen?")) delM.mutate(t.id); }}>×</button>
        </div>
      ))}
      {row(form, setForm)}
      <div className="jd-actions"><button className="jf-btn" disabled={!valid(form) || addM.isPending} onClick={() => addM.mutate(toNums(form))}>Tranche hinzufügen</button></div>
    </div>
  );
}

function Chart({ tradeId }: { tradeId: number }) {
  const [ctx, setCtx] = useState("6M");
  const { data, isLoading } = useQuery({
    queryKey: ["trade-chart", tradeId, ctx],
    queryFn: () => apiClient.get(`/journal/${tradeId}/chart?context=${ctx}`).then(r => r.data),
  });
  return (
    <div className="jd-sec">
      <div className="jd-sec-head"><span>Kursverlauf</span>
        <div className="jd-ctx">{["1M", "3M", "6M", "1J", "2J"].map(o =>
          <button key={o} className={ctx === o ? "on" : ""} onClick={() => setCtx(o)}>{o}</button>)}</div>
      </div>
      {isLoading ? <div className="jd-empty">Lädt …</div> : !data?.bars?.length ? <div className="jd-empty">Keine Kursdaten</div> : (
        <ResponsiveContainer width="100%" height={160}>
          <LineChart data={data.bars} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
            <XAxis dataKey="date" tick={{ fontSize: 10, fill: "var(--text-faint)" }} tickLine={false} axisLine={{ stroke: "var(--border)" }} minTickGap={40}
              tickFormatter={(v: string) => new Date(v).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" })} />
            <YAxis tick={{ fontSize: 10, fill: "var(--text-faint)" }} tickLine={false} axisLine={false} domain={["auto", "auto"]} width={44} />
            <Tooltip contentStyle={{ background: "var(--surface)", border: "1px solid var(--border-strong)", borderRadius: 6, fontSize: 12 }}
              formatter={(v: any) => [de(v), "Kurs"]} labelFormatter={(l: any) => new Date(String(l)).toLocaleDateString("de-DE")} />
            <Line type="monotone" dataKey="close" stroke="var(--bar)" dot={false} strokeWidth={1.5} isAnimationActive={false} />
            {(data.markers || []).map((m: any, i: number) => (
              <ReferenceLine key={i} x={m.date} stroke={m.type === "exit" ? "var(--red)" : "var(--green)"}
                strokeDasharray={m.type === "exit" ? "1 3" : "4 3"} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      )}
      {data?.markers?.length > 0 && <div className="jd-legend"><span className="jd-lg in">Einstieg</span><span className="jd-lg out">Ausstieg</span></div>}
    </div>
  );
}

export default function TradeDetail({ t, onEdit, onSettle, onDelete }: { t: any; onEdit: () => void; onSettle: () => void; onDelete: () => void }) {
  const qc = useQueryClient();
  const upM = useMutation({ mutationFn: (file: File) => api.uploadAtt(t.id, file, "analysis"), onSuccess: () => qc.invalidateQueries({ queryKey: ["journal"] }) });
  const delAtt = useMutation({ mutationFn: (aid: number) => api.deleteAtt(t.id, aid), onSuccess: () => qc.invalidateQueries({ queryKey: ["journal"] }) });
  const open = t.status === "open";
  const kv: [string, string][] = [
    [t.is_sparplan ? "Ø Einstieg" : "Einstieg", `${de(t.entry_price)} ${t.currency || ""}`],
    ...(t.exit_price != null ? [["Ausstieg", `${de(t.exit_price)} ${t.currency || ""}`] as [string, string]] : []),
    ["Stück", de(t.quantity, t.is_sparplan ? 4 : 0)],
    ...(t.pnl != null ? [["P&L", `${signed(t.pnl, 2, " " + (t.currency || "€"))} (${signed(t.pnl_pct)})`] as [string, string]] : []),
    ...(open && t.current_price ? [["Aktuell", `${de(t.current_price)} · ${signed((t.current_price - t.entry_price) * t.quantity, 2, " " + (t.currency || "€"))}`] as [string, string]] : []),
    ["Eröffnet", fmtDT(t.opened_at)],
    ...(t.closed_at ? [["Geschlossen", fmtDT(t.closed_at)] as [string, string]] : []),
    ...(t.sl_price != null ? [["SL / TP", `${de(t.sl_price)} / ${t.tp_price != null ? de(t.tp_price) : "–"}`] as [string, string]] : []),
    ["Gebühren", `${de((t.fees_entry || 0) + (t.fees_exit || 0))} €`],
    ...(t.tax ? [["Steuer", `${de(t.tax)} €`] as [string, string]] : []),
    ["Kategorie", labelOf(CATEGORIES, t.category)],
    ...(t.broker ? [["Broker", t.broker] as [string, string]] : []),
    ...(t.isin ? [["ISIN", t.isin] as [string, string]] : []),
    ...(t.underlying_symbol ? [["Underlying", `${t.underlying_symbol}${t.underlying_price_at_entry ? " @ " + de(t.underlying_price_at_entry) : ""}`] as [string, string]] : []),
    ...(t.signal_source ? [["Signal", `${t.sigmund_score != null ? "Score " + de(t.sigmund_score, 0) : "ja"}`] as [string, string]] : []),
    ...(t.signal_strategy ? [["Strategien", String(t.signal_strategy).split(", ").map(strategyLabel).join(", ")] as [string, string]] : []),
  ];
  return (
    <div className="jd">
      <dl className="jd-kv">{kv.map(([k, v]) => <div key={k}><dt>{k}</dt><dd className="mono">{v}</dd></div>)}</dl>
      {t.notes && <p className="jd-notes">{t.notes}</p>}
      {t.is_sparplan && <Tranchen tradeId={t.id} currency={t.currency || ""} />}
      <Chart tradeId={t.id} />
      <div className="jd-sec">
        <div className="jd-sec-head"><span>Anhänge</span>
          <label className="jf-btn">PDF / Bild hinzufügen
            <input type="file" accept=".pdf,.png,.jpg,.jpeg" multiple hidden
              onChange={e => { Array.from(e.target.files || []).forEach(f => upM.mutate(f)); e.target.value = ""; }} />
          </label>
        </div>
        {(t.attachments || []).length === 0 ? <div className="jd-empty">Keine Anhänge</div> :
          (t.attachments || []).map((a: any) => (
            <div key={a.id} className="jd-att"><span>{a.filename}</span>
              <button className="jf-mini" aria-label="Anhang löschen" onClick={() => { if (window.confirm(`${a.filename} löschen?`)) delAtt.mutate(a.id); }}>×</button></div>
          ))}
      </div>
      <div className="jd-actions">
        <button className="jf-btn" onClick={onEdit}>Bearbeiten</button>
        {open && !t.is_sparplan && <button className="jf-btn" onClick={onSettle}>Abrechnen</button>}
        <button className="jf-btn danger" onClick={onDelete}>Löschen</button>
      </div>
    </div>
  );
}
